import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import ForumInfo from "notifications/entities/forum-info/forum-info";
import ForumInfoSchema from "notifications/entities/forum-info/forum-info-schema";
import MessageContent from "notifications/values/message-content/message-content";
import MessageContentSchema from "notifications/values/message-content/message-content-schema";
import MessageSubject from "notifications/values/message-subject/message-subject";
import MessageSubjectSchema from "notifications/values/message-subject/message-subject-schema";
import RecipientName from "notifications/values/recipient-name/recipient-name";
import RecipientNameSchema from "notifications/values/recipient-name/recipient-name-schema";
import SenderName from "notifications/values/sender-name/sender-name";
import SenderNameSchema from "notifications/values/sender-name/sender-name-schema";
import { z } from "zod";

export type MessageInfoJSON = z.infer<typeof MessageInfoSchema.MessageInfoJSONZ>;
export type MessageInfoParams = z.infer<typeof MessageInfoSchema.MessageInfoZ>;

export default class MessageInfoSchema {
  public static readonly MessageInfoJSONZ = z.object({
    id: z.uuid(),
    senderId: z.uuid().nullish(),
    senderName: SenderNameSchema.SenderNameJSONZ.nullish(),
    recipientId: z.uuid().nullish(),
    recipientName: RecipientNameSchema.RecipientNameJSONZ,
    forum: ForumInfoSchema.ForumInfoJSONZ,
    content: MessageContentSchema.MessageContentJSONZ.nullish(),
    subject: MessageSubjectSchema.MessageSubjectJSONZ.nullish(),
  });
  public static readonly MessageInfoZ = z.object({
    id: z.instanceof(Guid),
    senderId: z.instanceof(Guid).nullish(),
    senderName: z.instanceof(SenderName).optional(),
    recipientId: z.instanceof(Guid).nullish(),
    recipientName: z.instanceof(RecipientName),
    forumInfo: z.instanceof(ForumInfo),
    content: z.instanceof(MessageContent).optional(),
    subject: z.instanceof(MessageSubject).optional(),
  });

  public static validate(values: unknown): MessageInfoParams {
    return parseOrThrow(MessageInfoSchema.MessageInfoZ, values, {
      logError: true,
      schemaName: "MessageInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): MessageInfoJSON {
    return parseOrThrow(MessageInfoSchema.MessageInfoJSONZ, values, {
      logError: true,
      schemaName: "MessageInfoSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is MessageInfoParams {
    try {
      MessageInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MessageInfoJSON {
    try {
      MessageInfoSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): MessageInfoParams {
    const parsed = parseOrThrow(MessageInfoSchema.MessageInfoJSONZ, data, {
      logError: true,
      schemaName: "MessageInfoSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return MessageInfoSchema.codec.decode(parsed);
  }

  public static serialize(messageInfo: MessageInfoParams): MessageInfoJSON {
    const parsed = MessageInfoSchema.validate(messageInfo);
    return MessageInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(MessageInfoSchema.MessageInfoJSONZ, MessageInfoSchema.MessageInfoZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      recipientName: new RecipientName(RecipientNameSchema.deserialize(data.recipientName)),
      forumInfo: new ForumInfo(ForumInfoSchema.deserialize(data.forum)),
      content: data.content ? MessageContentSchema.deserialize(data.content) : undefined,
      subject: data.subject ? MessageSubjectSchema.deserialize(data.subject) : undefined,
      senderId: Guid.fromJSON(data.senderId) ?? null,
      senderName: data.senderName ? new SenderName(data.senderName) : undefined,
      recipientId: Guid.fromJSON(data.recipientId) ?? null,
    }),
    encode: (messageInfo) => ({
      id: messageInfo.id.toJSON(),
      recipientName: messageInfo.recipientName.toJSON(),
      forum: messageInfo.forumInfo.toJSON(),
      content: messageInfo.content?.toJSON(),
      subject: messageInfo.subject?.toJSON(),
      senderId: messageInfo.senderId?.toJSON() ?? null,
      senderName: messageInfo.senderName?.toJSON(),
      recipientId: messageInfo.recipientId?.toJSON() ?? null,
    }),
  });
}
